import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Flex, Heading, Text, Button, SimpleGrid, Card, CardHeader,
  CardBody, Icon, Badge, Spinner, Alert, AlertIcon, Divider, Link, Wrap, WrapItem,
} from '@chakra-ui/react';
import { ArrowBackIcon, DownloadIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { MdSportsSoccer, MdEventAvailable, MdAssignmentTurnedIn } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/AxiosInstance';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

const ESTATUS_COLORS: Record<string, string> = {
  Publicada: 'green', Abierta: 'green', Vigente: 'green',
  Cerrada: 'red', Borrador: 'gray', Finalizada: 'purple',
};

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString('es-MX', { dateStyle: 'long' }) : '—';

const ConvocatoriasDeportesPublico: React.FC = () => {
  const navigate = useNavigate();
  const [convocatorias, setConvocatorias] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/deportes/convocatorias', {
        params: { publicadaPortal: true },
      });
      const lista: any[] = Array.isArray(data) ? data : (data?.data || []);
      // Enriquecer con archivos públicos (el listado no los incluye)
      const conArchivos = await Promise.all(
        lista.map(async (c) => {
          try {
            const det = await axiosInstance.get(`/deportes/convocatorias/${c.id}`);
            const archivos = (det.data?.archivos || []).filter(
              (a: any) => a.publicoPortal !== false,
            );
            return { ...c, archivos };
          } catch {
            return { ...c, archivos: [] };
          }
        }),
      );
      setConvocatorias(conArchivos);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'No se pudieron cargar las convocatorias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header institucional */}
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdSportsSoccer} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Convocatorias Deportivas</Heading>
            <Text fontSize="sm" opacity={0.85}>Dirección de Deportes</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={8} px={{ base: 4, md: 10 }} maxW="1000px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" wrap="wrap">
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500"
              onClick={() => navigate('/portal/2026')}>
              Portal Ciudadano
            </Button>
            <Button size="sm" colorScheme="teal" leftIcon={<Icon as={MdAssignmentTurnedIn} />}
              onClick={() => navigate('/portal/deportes/inscripcion')}>
              Inscribirme
            </Button>
          </HStack>

          {loading && (
            <Flex justify="center" py={16}><Spinner size="xl" color="#5b182b" thickness="4px" /></Flex>
          )}

          {!loading && error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text>{error}</Text>
                <Button mt={2} size="xs" onClick={cargar}>Reintentar</Button>
              </Box>
            </Alert>
          )}

          {!loading && !error && convocatorias.length === 0 && (
            <Card shadow="sm">
              <CardBody textAlign="center" py={12}>
                <Icon as={MdEventAvailable} boxSize={12} color="gray.300" mb={3} />
                <Heading size="sm" color="gray.500">No hay convocatorias vigentes</Heading>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  Por el momento no existen convocatorias publicadas. Vuelva a consultar más tarde.
                </Text>
              </CardBody>
            </Card>
          )}

          {!loading && !error && convocatorias.length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {convocatorias.map((c) => (
                <Card key={c.id} shadow="md" _hover={{ shadow: 'lg' }} transition="all 0.2s" display="flex" flexDir="column">
                  <CardHeader bg="gray.50" borderTopRadius="md" pb={3}>
                    <HStack justify="space-between" align="start">
                      <Box>
                        <Heading size="sm" color="#5b182b">{c.nombre}</Heading>
                        {c.folio && <Text fontFamily="mono" fontSize="xs" color="gray.400">{c.folio}</Text>}
                      </Box>
                      <Badge colorScheme={ESTATUS_COLORS[c.estatus] || 'gray'} borderRadius="full" px={3}>
                        {c.estatus || 'Publicada'}
                      </Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody display="flex" flexDir="column" flex="1">
                    <VStack align="stretch" spacing={3} flex="1">
                      {c.disciplina?.nombre && (
                        <HStack>
                          <Icon as={MdSportsSoccer} color="green.500" />
                          <Text fontSize="sm" fontWeight="medium">{c.disciplina.nombre}</Text>
                        </HStack>
                      )}
                      {c.descripcion && (
                        <Text fontSize="sm" color="gray.600" noOfLines={3}>{c.descripcion}</Text>
                      )}

                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="xs" color="gray.500">PUBLICACIÓN</Text>
                          <Text fontSize="sm">{fmtDate(c.fechaPublicacion)}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500">CIERRE</Text>
                          <Text fontSize="sm">{fmtDate(c.fechaCierre)}</Text>
                        </Box>
                      </SimpleGrid>

                      {Array.isArray(c.categorias) && c.categorias.length > 0 && (
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb={1}>CATEGORÍAS</Text>
                          <Wrap>
                            {c.categorias.map((cc: any, i: number) => (
                              <WrapItem key={i}>
                                <Badge colorScheme="teal" variant="subtle">
                                  {cc?.categoria?.nombre || cc?.nombre || '—'}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Box>
                      )}

                      {c.requisitos && (
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb={1}>REQUISITOS</Text>
                          <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">{c.requisitos}</Text>
                        </Box>
                      )}

                      {Array.isArray(c.archivos) && c.archivos.length > 0 && (
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb={1}>DOCUMENTOS</Text>
                          <VStack align="stretch" spacing={1}>
                            {c.archivos.map((a: any) => (
                              <Link key={a.id} href={`${API_URL}${a.rutaArchivo}`} isExternal
                                fontSize="sm" color="blue.600">
                                <HStack spacing={2}>
                                  <DownloadIcon />
                                  <Text noOfLines={1}>{a.nombreArchivo || 'Documento'}</Text>
                                  <ExternalLinkIcon boxSize={3} />
                                </HStack>
                              </Link>
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>

                    <Divider my={3} />
                    <Button colorScheme="teal" size="sm" w="full"
                      onClick={() => navigate(`/portal/deportes/inscripcion?convocatoriaId=${c.id}`)}>
                      Inscribirme a esta convocatoria →
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}

          <Text fontSize="xs" color="gray.400" textAlign="center" pt={4}>
            Dirección de Deportes · H. Ayuntamiento de Totolac · Tlaxcala
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default ConvocatoriasDeportesPublico;
